import React, { useState, useEffect } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { formatDate } from '../../utils/helpers';
import { NewCycleModal, CompleteCycleModal } from './NewCycleModal';
import { ElectricityBillModal, MaintenanceLogModal } from './Pet400Modals';

const TABS = [
  { id: 'sessions',    label: '🔥 Chambers & Cycles' },
  { id: 'electricity', label: '⚡ Electricity' },
  { id: 'maintenance', label: '🛠️ Maintenance' },
  { id: 'analytics',  label: '📊 Analytics' },
];

const OUTCOME_STYLE = {
  Light:   'bg-gray-100 text-gray-700',
  Greenish: 'bg-green-100 text-green-800',
  Dark:    'bg-gray-800 text-white',
};

const Pet400View = ({ onClose }) => {
  const [activeTab, setActiveTab]         = useState('sessions');
  const [sessions, setSessions]           = useState([]);
  const [bills, setBills]                 = useState([]);
  const [logs, setLogs]                   = useState([]);
  const [analytics, setAnalytics]         = useState(null);
  const [chambersBoard, setChambersBoard] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showNewCycle, setShowNewCycle]           = useState(false);
  const [newCycleChamber, setNewCycleChamber]     = useState(null);
  const [completingSession, setCompletingSession] = useState(null);
  const [showElecBill, setShowElecBill]           = useState(false);
  const [showMaint, setShowMaint]                 = useState(false);
  const [expandedSessions, setExpandedSessions] = useState({});
  const [analyticsPreset, setAnalyticsPreset]     = useState('all');
  const [analyticsRange, setAnalyticsRange]       = useState({ start: '', end: '' });
  const dateRangeError = !!(analyticsRange.start && analyticsRange.end && analyticsRange.start > analyticsRange.end);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, b, l, a, ch] = await Promise.all([
        apiClient.get('/pet400/sessions?limit=200'),
        apiClient.get('/pet400/electricity-bills'),
        apiClient.get('/pet400/maintenance-logs'),
        apiClient.get('/pet400/analytics'),
        apiClient.get('/pet400/chambers'),
      ]);
      setSessions(s.data.sessions || []);
      setBills(b.data.bills || []);
      setLogs(l.data.logs || []);
      setAnalytics(a.data);
      setChambersBoard(ch.data.chambers || []);
      // Auto-expand running sessions
      const running = {};
      (s.data.sessions || []).forEach(sess => {
        if (sess.status === 'running') running[sess.id] = true;
      });
      setExpandedSessions(running);
    } catch { showToast('Failed to load data', 'error'); }
    finally { setLoading(false); }
  };

  // Load analytics with optional date range
  const loadAnalytics = async (start = analyticsRange.start, end = analyticsRange.end) => {
    try {
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end)   params.append('end_date', end);
      const res = await apiClient.get(`/pet400/analytics?${params.toString()}`);
      setAnalytics(res.data);
    } catch { showToast('Failed to load analytics', 'error'); }
  };

  // Auto-apply custom date range when both dates are populated and valid (debounced)
  useEffect(() => {
    if (analyticsPreset !== 'custom') return;
    if (!analyticsRange.start || !analyticsRange.end) return;
    if (analyticsRange.start > analyticsRange.end) return; // reversed — show warning, don't fetch
    const t = setTimeout(() => loadAnalytics(analyticsRange.start, analyticsRange.end), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsRange.start, analyticsRange.end, analyticsPreset]);

  const getAESTDate = (offsetDays = 0) => {
    const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
    d.setDate(d.getDate() + offsetDays);
    return d.toISOString().split('T')[0];
  };

  const applyPreset = (key) => {
    setAnalyticsPreset(key);
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const today = fmt(now);
    let start = '', end = today;

    if (key === 'this_month') {
      start = fmt(new Date(now.getFullYear(), now.getMonth(), 1));
    } else if (key === 'last_month') {
      start = fmt(new Date(now.getFullYear(), now.getMonth()-1, 1));
      end   = fmt(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (key === 'last_3') {
      const d = new Date(now); d.setMonth(d.getMonth()-3);
      start = fmt(new Date(d.getFullYear(), d.getMonth(), 1));
    } else if (key === 'last_6') {
      const d = new Date(now); d.setMonth(d.getMonth()-6);
      start = fmt(new Date(d.getFullYear(), d.getMonth(), 1));
    } else if (key === 'this_year') {
      start = `${now.getFullYear()}-01-01`;
    } else {
      start = ''; end = '';
    }

    setAnalyticsRange({ start, end });
    loadAnalytics(start, end);
  };

  useEffect(() => { loadData(); }, []);

  const del = async (endpoint, id, msg) => {
    if (!window.confirm(`Delete this ${msg}?`)) return;
    try { await apiClient.delete(`${endpoint}/${id}`); showToast('Deleted'); loadData(); }
    catch { showToast('Failed to delete', 'error'); }
  };

  const exportSessionsCSV = () => {
    if (!sessions.length) { showToast('No sessions to export', 'error'); return; }

    const headers = [
      'Cycle #', 'Date', 'Chamber', 'Status',
      'Total Pets', 'Dogs', 'Cats', 'Other',
      'Weight (kg)', 'Gas (MJ)', 'Temp (°C)',
      'Start Time', 'End Time',
      'Electricity Cost ($)', 'Total Cost ($)', 'Cost/Pet ($)',
      'Cycle Hours', 'Ashes Outcome', 'Notes',
      'Microchips Expected', 'Microchips Recovered'
    ];

    const rows = sessions.map(s => {
      const chipped = (s.microchip_recovery || []).filter(c => c.microchip);
      const recovered = chipped.filter(c => c.recovered).length;
      return [
        s.cycle_number,
        s.date,
        s.chamber || '',
        s.status,
        s.total_pets || 0,
        s.dogs_count || 0,
        s.cats_count || 0,
        s.other_count || 0,
        s.tissue_weight_kg || 0,
        s.gas_usage_mj || '',
        s.operating_temp_c || '',
        s.start_time || '',
        s.end_time || '',
        (s.electricity_cost || 0).toFixed(2),
        (s.total_cost || 0).toFixed(2),
        (s.cost_per_pet || 0).toFixed(2),
        s.cycle_hours || '',
        s.ashes_outcome || '',
        (s.notes || '').replace(/,/g, ';'),
        chipped.length,
        recovered
      ];
    });

    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${v}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pet400-cycles-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`✅ Exported ${sessions.length} cycles`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-center z-40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-5xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[92vh]">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-indigo-700 px-5 py-4 sm:rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <div>
            <h1 className="text-white font-bold text-xl">🔥 Cremators Operations</h1>
            <p className="text-blue-200 text-xs mt-0.5">
              {analytics?.summary?.total_sessions || 0} sessions · {analytics?.summary?.total_pets || 0} pets processed
            </p>
          </div>
          <button onClick={onClose} className="text-white text-3xl font-bold hover:text-blue-200 leading-none">×</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto flex-shrink-0">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === t.id
                  ? 'border-b-2 border-blue-600 text-blue-700 bg-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* ── SESSIONS ── */}
              {activeTab === 'sessions' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Cremation Cycles</h2>
                    <div className="flex items-center gap-2">
                      <button onClick={exportSessionsCSV}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5">
                        Export
                      </button>
                      <button onClick={() => setShowNewCycle(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                        + New Cycle
                      </button>
                    </div>
                  </div>

                  {/* ── CHAMBER BOARD — 8 individual + 1 communal/equine ── */}
                  <div className="mb-5" data-testid="chamber-board">
                    <p className="text-xs font-medium text-gray-600 mb-2">🏭 Chamber Board — tap an idle chamber to start a cycle, tap a running one to complete it</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {chambersBoard.map(c => {
                        const busy = c.status === 'in_use';
                        const sess = c.active_session;
                        return (
                          <button key={c.name}
                            data-testid={`chamber-tile-${c.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                            onClick={() => {
                              if (busy) {
                                const full = sessions.find(x => x.id === sess?.id);
                                if (full) setCompletingSession(full);
                              } else {
                                setNewCycleChamber(c.name);
                                setShowNewCycle(true);
                              }
                            }}
                            className={`text-left rounded-xl border-2 p-3 transition-all ${
                              c.is_communal ? 'col-span-2 sm:col-span-3 lg:col-span-4 ' : ''
                            }${
                              busy
                                ? 'bg-orange-50 border-orange-400 hover:border-orange-500 shadow-sm'
                                : 'bg-green-50 border-green-200 hover:border-green-400'
                            }`}>
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-sm text-gray-900">
                                {c.is_communal ? '🐴' : '🔥'} {c.name}
                              </span>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                busy ? 'bg-orange-200 text-orange-800 animate-pulse' : 'bg-green-200 text-green-800'
                              }`}>
                                {busy ? 'IN USE' : 'IDLE'}
                              </span>
                            </div>
                            {busy && sess ? (
                              <p className="text-xs text-orange-700 mt-1 truncate">
                                #{sess.cycle_number} · {(sess.pet_names || []).join(', ') || `${sess.total_pets} pet(s)`}
                                {sess.start_time ? ` · ${sess.start_time}` : ''}
                              </p>
                            ) : (
                              <p className="text-xs text-green-600 mt-1">
                                {c.is_communal ? 'Communal & equine — multiple pets' : 'Private — 1 pet per cycle'}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {sessions.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      <p className="text-5xl mb-3">🔥</p>
                      <p className="text-lg font-medium">No cycles recorded yet</p>
                      <p className="text-sm mt-1">Tap an idle chamber above to start your first cycle</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Sort: running first, then by date */}
                      {[...sessions]
                        .sort((a, b) => {
                          if (a.status === 'running' && b.status !== 'running') return -1;
                          if (b.status === 'running' && a.status !== 'running') return 1;
                          return 0;
                        })
                        .map(s => {
                        const isExpanded = expandedSessions[s.id];
                        const isRunning = s.status === 'running';
                        return (
                          <div key={s.id} className={`border rounded-xl transition-shadow ${
                            isRunning ? 'border-blue-400 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:shadow-sm'
                          }`}>
                            {/* Collapsed Header — always visible */}
                            <div
                              className="p-3 cursor-pointer"
                              onClick={() => setExpandedSessions(prev => ({ ...prev, [s.id]: !prev[s.id] }))}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                                  <span className="font-bold text-gray-900">#{s.cycle_number}</span>
                                  <span className="text-xs text-gray-500">{formatDate(s.date)}</span>
                                  {s.chamber && (
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${s.chamber === 'Communal / Equine' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                                      {s.chamber === 'Communal / Equine' ? '🐴 ' : '🔥 '}{s.chamber}
                                    </span>
                                  )}
                                  {isRunning ? (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-300 animate-pulse">
                                      Running
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Done</span>
                                  )}
                                  {s.ashes_outcome && (
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${OUTCOME_STYLE[s.ashes_outcome] || 'bg-gray-100 text-gray-700'}`}>
                                      {s.ashes_outcome}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <span className="text-sm text-gray-600">🐾 {s.total_pets}</span>
                                  <span className="text-sm font-semibold text-gray-900">${s.total_cost?.toFixed(2)}</span>
                                  <span className="text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                                </div>
                              </div>
                              {/* Pet names preview on collapsed */}
                              {s.pet_details?.length > 0 && !isExpanded && (
                                <p className="text-xs text-gray-400 mt-1 truncate">
                                  {s.pet_details.map(p => p.pet_name).join(', ')}
                                </p>
                              )}
                            </div>

                            {/* Expanded Details */}
                            {isExpanded && (
                              <div className="px-3 pb-3 border-t border-gray-100 pt-3 space-y-3">
                                {/* Pet Names */}
                                {s.pet_details?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 mb-1">Pets in this cycle</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {s.pet_details.map((p, i) => (
                                        <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                                          {p.pet_name} ({p.pet_type || 'Pet'})
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Details grid */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                                  <div className="bg-gray-50 rounded p-2">
                                    <p className="text-xs text-gray-500">Pets</p>
                                    <p className="font-medium">{s.dogs_count || 0}🐕 {s.cats_count || 0}🐈{s.other_count > 0 ? ` ${s.other_count}🐾` : ''}</p>
                                  </div>
                                  <div className="bg-gray-50 rounded p-2">
                                    <p className="text-xs text-gray-500">Weight</p>
                                    <p className="font-medium">{s.tissue_weight_kg}kg</p>
                                  </div>
                                  <div className="bg-gray-50 rounded p-2">
                                    <p className="text-xs text-gray-500">Gas / Fuel</p>
                                    <p className="font-medium">{s.gas_usage_mj ? `${s.gas_usage_mj} MJ` : '—'}</p>
                                  </div>
                                  <div className="bg-gray-50 rounded p-2">
                                    <p className="text-xs text-gray-500">Temp</p>
                                    <p className="font-medium">{s.operating_temp_c ? `${s.operating_temp_c}°C` : '—'}</p>
                                  </div>
                                </div>

                                {/* Times */}
                                {(s.start_time || s.end_time) && (
                                  <p className="text-xs text-gray-500">
                                    🕐 {s.start_time ? `Started ${s.start_time}` : ''}{s.start_time && s.end_time ? ' → ' : ''}{s.end_time ? `Finished ${s.end_time}` : ''}
                                  </p>
                                )}

                                {/* Costs */}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                  <span className="font-semibold text-gray-900">
                                    Total: ${s.total_cost?.toFixed(2)}
                                    <span className="text-blue-600 font-normal ml-1">(${s.cost_per_pet?.toFixed(2)}/pet)</span>
                                  </span>
                                  {s.cycle_hours && <span className="text-gray-600">⏱️ {s.cycle_hours}h</span>}
                                  {s.electricity_status === 'pending' && (
                                    <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">⚡ Elec pending</span>
                                  )}
                                </div>

                                {/* Microchip recovery */}
                                {s.status === 'completed' && s.microchip_recovery?.length > 0 && (() => {
                                  const chipped = s.microchip_recovery.filter(c => c.microchip);
                                  const recovered = chipped.filter(c => c.recovered).length;
                                  const missing = chipped.filter(c => !c.recovered);
                                  const last4 = (chip) => chip ? `…${chip.replace(/\D/g,'').slice(-4)}` : '';
                                  return (
                                    <div>
                                      {missing.length > 0 ? (
                                        <span className="text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full">
                                          🔬 {recovered}/{chipped.length} chips — ❌ {missing.map(c => `${c.pet_name} (${last4(c.microchip)})`).join(', ')}
                                        </span>
                                      ) : (
                                        <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">
                                          🔬 {recovered}/{chipped.length} chips recovered ✅
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}

                                {s.notes && <p className="text-xs text-gray-400 italic">📝 {s.notes}</p>}

                                {/* Actions */}
                                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                  {isRunning && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setCompletingSession(s); }}
                                      className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                                      ✅ Complete Cycle
                                    </button>
                                  )}
                                  {!isRunning && <div />}
                                  <button onClick={(e) => { e.stopPropagation(); del('/pet400/sessions', s.id, 'cycle record'); }}
                                    className="text-gray-300 hover:text-red-400 text-sm">Delete</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── ELECTRICITY ── */}
              {activeTab === 'electricity' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Quarterly Electricity Bills</h2>
                    <button onClick={() => setShowElecBill(true)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                      ⚡ Load Bill
                    </button>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
                    💡 Loading a quarterly bill automatically distributes the electricity cost across all sessions in that period.
                  </div>
                  {bills.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <p className="text-4xl mb-2">⚡</p>
                      <p>No electricity bills loaded yet</p>
                      <p className="text-sm mt-1">Load your first quarterly bill to see electricity cost per session</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {bills.map(b => (
                        <div key={b.id} className="bg-white border border-gray-200 rounded-xl p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-gray-900">{b.bill_reference}</span>
                                <span className="text-sm text-gray-500">{b.supplier}</span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                📅 {formatDate(b.period_start)} → {formatDate(b.period_end)}
                              </p>
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-2 text-sm">
                                <span>Total: <strong>${b.total_amount?.toFixed(2)}</strong></span>
                                {b.government_rebate > 0 && (
                                  <span className="text-green-600">Rebate: −${b.government_rebate?.toFixed(2)}</span>
                                )}
                                <span className="text-blue-700 font-semibold">Net: ${b.net_amount?.toFixed(2)}</span>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-sm text-gray-600">
                                <span>📊 {b.sessions_count} sessions allocated</span>
                                <span className="text-orange-600 font-medium">${b.cost_per_session?.toFixed(2)}/session</span>
                                {b.usage_kwh && <span>⚡ {b.usage_kwh?.toLocaleString()} kWh</span>}
                              </div>
                            </div>
                            <button onClick={() => del('/pet400/electricity-bills', b.id, 'electricity bill')}
                              className="text-gray-300 hover:text-red-400 text-xl flex-shrink-0">🗑</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── MAINTENANCE ── */}
              {activeTab === 'maintenance' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Maintenance Log</h2>
                    <button onClick={() => setShowMaint(true)}
                      className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                      + Record
                    </button>
                  </div>
                  {logs.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <p className="text-4xl mb-2">🛠️</p>
                      <p>No maintenance logs yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {logs.map(log => (
                        <div key={log.id} className={`rounded-xl border p-3 ${
                          log.log_type === 'effluent'
                            ? log.cost_status === 'pending' ? 'bg-amber-50 border-amber-300' : 'bg-teal-50 border-teal-200'
                            : 'bg-purple-50 border-purple-200'
                        }`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm">
                                  {log.log_type === 'effluent' ? '🛠️ Service' : '🧹 Chamber Clean'}
                                </span>
                                <span className="text-xs text-gray-500">{formatDate(log.date)}</span>
                                {log.log_type === 'effluent' && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${log.cost_status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
                                    {log.cost_status === 'pending' ? '⚠️ Cost pending' : '✅ Allocated'}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-600">
                                {log.supplier && <span>🏢 {log.supplier}</span>}
                                {log.litres   && <span>🔥 {log.litres?.toLocaleString()}L</span>}
                                {log.cost     && <span className="text-teal-700 font-semibold">💰 ${log.cost?.toFixed(2)}</span>}
                                {log.allocated_sessions > 0 && <span className="text-gray-400">{log.allocated_sessions} sessions · ${log.cost_per_session?.toFixed(2)}/session</span>}
                              </div>
                              {log.notes && <p className="text-xs text-gray-400 mt-0.5 italic">{log.notes}</p>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              
                              <button onClick={() => del('/pet400/maintenance-logs', log.id, 'log entry')}
                                className="text-gray-300 hover:text-red-400 text-lg">🗑</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── ANALYTICS ── */}
              {activeTab === 'analytics' && (
                <div>
                  {/* Date Range Presets */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5">
                    <p className="text-xs font-medium text-gray-600 mb-2">📅 Date Range</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        { label: 'This Month',    key: 'this_month' },
                        { label: 'Last Month',    key: 'last_month' },
                        { label: 'Last 3 Months', key: 'last_3' },
                        { label: 'Last 6 Months', key: 'last_6' },
                        { label: 'This Year',     key: 'this_year' },
                        { label: 'All Time',      key: 'all' },
                      ].map(({ label, key }) => (
                        <button key={key} onClick={() => applyPreset(key)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                            analyticsPreset === key
                              ? 'bg-blue-700 text-white'
                              : 'bg-white border border-gray-300 text-gray-600 hover:bg-blue-50 hover:border-blue-300'
                          }`}>{label}</button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">From</label>
                        <input type="date" value={analyticsRange.start}
                          onChange={e => { setAnalyticsRange(r => ({...r, start: e.target.value})); setAnalyticsPreset('custom'); }}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400" />
                      </div>
                      <span className="text-gray-400 mt-4">→</span>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">To</label>
                        <input type="date" value={analyticsRange.end}
                          onChange={e => { setAnalyticsRange(r => ({...r, end: e.target.value})); setAnalyticsPreset('custom'); }}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400" />
                      </div>
                      {analyticsPreset === 'custom' && analyticsRange.start && analyticsRange.end && !dateRangeError && (
                        <div className="mt-4 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded-lg" data-testid="analytics-active-range-badge">
                          📅 Showing: {analyticsRange.start} → {analyticsRange.end}
                        </div>
                      )}
                    </div>
                    {dateRangeError && (
                      <p className="text-xs text-red-700 mt-2 bg-red-50 border border-red-200 rounded p-2" data-testid="date-range-error">
                        ⚠️ End date must be on or after Start date.
                      </p>
                    )}
                    {analytics && analytics.summary?.pending_electricity > 0 && (
                      <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                        ⚠️ {analytics.summary.pending_electricity} session(s) have pending electricity costs — totals will update when bills are loaded.
                      </p>
                    )}
                  </div>

                  {analytics ? (
                    <>
                      {/* Scoreboard */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {[
                          { label: 'Cycles',         val: analytics.summary?.total_sessions,                            color: 'blue'   },
                          { label: 'Pets Processed', val: analytics.summary?.total_pets,                                 color: 'green'  },
                          { label: 'Avg Cost / Pet', val: `$${analytics.summary?.avg_cost_per_pet?.toFixed(2)}`,         color: 'orange' },
                          { label: 'Avg Pets/Cycle', val: analytics.summary?.avg_pets_per_session,                      color: 'purple' },
                        ].map(c => (
                          <div key={c.label} className={`bg-${c.color}-50 border border-${c.color}-200 rounded-xl p-3`}>
                            <p className={`text-xs text-${c.color}-600 font-medium`}>{c.label}</p>
                            <p className={`text-xl font-bold text-${c.color}-800 mt-1`}>{c.val ?? '—'}</p>
                          </div>
                        ))}
                      </div>

                      {/* Cost breakdown */}
                      {analytics.cost_breakdown && Object.keys(analytics.cost_breakdown).length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
                          <h3 className="font-semibold text-gray-700 text-sm mb-3">
                            💰 Cost Breakdown — Total: ${analytics.summary?.total_cost?.toLocaleString('en-AU',{minimumFractionDigits:2})}
                          </h3>
                          <div className="space-y-3">
                            {[
                              { key: 'electricity', label: '⚡ Electricity', bar: 'bg-yellow-400' },
                            ].map(({ key, label, bar }) => {
                              const item = analytics.cost_breakdown[key];
                              if (!item || item.amount === 0) return null;
                              return (
                                <div key={key}>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">{label}</span>
                                    <span className="font-medium">${item.amount?.toLocaleString('en-AU',{minimumFractionDigits:2})} <span className="text-gray-400 text-xs">({item.pct}%)</span></span>
                                  </div>
                                  <div className="w-full bg-gray-100 rounded-full h-2">
                                    <div className={`${bar} h-2 rounded-full transition-all`} style={{width:`${Math.max(item.pct,1)}%`}}></div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Ashes quality */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        {analytics.summary?.ashes_outcomes && Object.keys(analytics.summary.ashes_outcomes).length > 0 && (
                          <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <h3 className="font-semibold text-gray-700 text-sm mb-2">🏺 Ashes Quality</h3>
                            <div className="flex gap-3 flex-wrap">
                              {[
                                { key:'Light',    dot:'bg-gray-100 border-gray-300',  emoji:'⬜' },
                                { key:'Greenish', dot:'bg-green-200 border-green-500', emoji:'🟢' },
                                { key:'Dark',     dot:'bg-gray-800 border-gray-700',   emoji:'⬛' },
                              ].map(({ key, dot, emoji }) => {
                                const count = analytics.summary.ashes_outcomes[key] || 0;
                                if (!count) return null;
                                const total = Object.values(analytics.summary.ashes_outcomes).reduce((a,b)=>a+b,0);
                                return (
                                  <div key={key} className="text-center">
                                    <p className="text-xl font-bold text-gray-800">{count}</p>
                                    <div className={`text-xs px-1.5 py-0.5 rounded border ${dot} whitespace-nowrap`}>{emoji} {key}</div>
                                    <p className="text-xs text-gray-400 mt-0.5">{((count/total)*100).toFixed(0)}%</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        </div>

                      {/* Microchip Recovery Analytics */}
                      {analytics.summary?.microchip_recovery?.total_chips > 0 && (
                        <div className="bg-white border border-purple-200 rounded-xl p-4 mb-4">
                          <h3 className="font-semibold text-gray-700 text-sm mb-3">🔬 Microchip Recovery</h3>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-purple-700">
                                {analytics.summary.microchip_recovery.recovery_rate_pct}%
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">Recovery rate</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-green-700">{analytics.summary.microchip_recovery.recovered}</p>
                              <p className="text-xs text-gray-500 mt-0.5">Chips found</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-red-600">{analytics.summary.microchip_recovery.not_found}</p>
                              <p className="text-xs text-gray-500 mt-0.5">Not found</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-gray-400">{analytics.summary.microchip_recovery.no_chip_on_file}</p>
                              <p className="text-xs text-gray-500 mt-0.5">No chip on file</p>
                            </div>
                          </div>
                          {analytics.summary.microchip_recovery.not_found > 0 && (
                            <p className="text-xs text-red-600 mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                              ⚠️ {analytics.summary.microchip_recovery.not_found} chip(s) not recovered — check individual cycle records for details
                            </p>
                          )}
                        </div>
                      )}

                      {/* Monthly table */}
                      {analytics.monthly?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                          <h3 className="font-semibold text-gray-700 text-sm p-4 border-b bg-gray-50">Monthly Breakdown</h3>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-50 text-xs text-gray-500 font-medium">
                                  {['Month','Cycles','Pets','Weight kg','⚡ Elec','Total','$/Pet','Avg Hrs'].map(h => (
                                    <th key={h} className="px-3 py-2.5 text-left whitespace-nowrap">{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {[...analytics.monthly].reverse().map(m => (
                                  <tr key={m.month} className="hover:bg-blue-50 transition-colors">
                                    <td className="px-3 py-2 font-semibold text-gray-900">{m.month}</td>
                                    <td className="px-3 py-2 text-gray-600">{m.sessions}</td>
                                    <td className="px-3 py-2 text-gray-600">{m.pets}</td>
                                    <td className="px-3 py-2 text-gray-600">{m.tissue_kg}</td>
                                    <td className="px-3 py-2 text-yellow-700">${m.electricity?.toFixed(0)}</td>
                                    <td className="px-3 py-2 font-bold text-gray-900">${m.total_cost?.toFixed(0)}</td>
                                    <td className="px-3 py-2 font-semibold text-blue-700">${m.cost_per_pet?.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-gray-500">{m.avg_cycle_hours ? `${m.avg_cycle_hours}h` : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <p className="text-3xl mb-2">📊</p>
                      <p className="text-sm">Loading analytics...</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showNewCycle      && <NewCycleModal        preselectedChamber={newCycleChamber} onClose={() => { setShowNewCycle(false); setNewCycleChamber(null); }} onSuccess={loadData} />}
      {completingSession && <CompleteCycleModal   session={completingSession} onClose={() => setCompletingSession(null)} onSuccess={loadData} />}
      {showElecBill      && <ElectricityBillModal  onClose={() => setShowElecBill(false)}          onSuccess={loadData} />}
            {showMaint         && <MaintenanceLogModal   onClose={() => setShowMaint(false)}              onSuccess={loadData} />}
          </div>
  );
};

export default Pet400View;
